/**
 * QuotesAdmin (Settings → Quotes) — the catalog behind the Good/Better/Best
 * equipment quote builder, pulled over from Seth's proposal app and made
 * self-service: Financing Plans (the GreenSky-style plans techs offer) +
 * Utility Rebates (the MN utilities a rebate can come from). Add / edit /
 * archive, no code edits. Reads/writes pegasus_financing_plan + pegasus_utility.
 */
import { useMemo, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { CreditCard, Zap, Check, Pencil, Plus, RotateCcw, X, Factory, ArrowUp, ArrowDown, Layers, CalendarClock, ListOrdered } from "lucide-react";
import { supabase } from "@/lib/supabase";
import { useOrgSettingNumber, useSaveOrgSetting } from "@/lib/orgSettings";
import { Card } from "@/components/ui/Card";
import { InfoTip } from "@/components/InfoTip";
import { useCan } from "@/lib/permissions/PermissionProvider";
import {
  useFinancingPlans, useSaveFinancingPlan,
  useUtilities, useSaveUtility,
  useQuoteBrands, useSaveQuoteBrand,
  useQuoteCategories, useSaveQuoteCategory,
  useQuotePackages, useSaveQuotePackage, useDeleteQuotePackage, pkgTotal,
  CATEGORY_TYPES, CATEGORY_TYPE_LABEL, APPLIES_TO, APPLIES_TO_LABEL,
  type FinancingPlan, type QuoteBrand, type QuoteCategory, type Utility,
  type QuotePackage, type QuotePackageLine,
} from "@/features/quotes/quotesAdmin";
import { useEquipTypes, useAccessorySubcategories, useItems, useEquipmentItems, type Item } from "@/features/items/items";

const CAT_ICON_NAMES = ["Flame", "Snowflake", "Wind", "Droplet", "Wrench", "Settings2", "Thermometer", "Zap", "Plug", "Package"];

const money = (n: number) =>
  n.toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 });

export function QuotesSection() {
  return (
    <div className="space-y-4">
      <ProposalTermsCard />
      <PackageOrderingCard />
      <PackagesCard />
      <BrandsCard />
      <CategoriesCard />
      <FinancingPlansCard />
      <UtilitiesCard />
    </div>
  );
}

/* ── GBB-ORDER-01 — the proposal app's PACKAGE ORDERING, ported: three
   ranked lists per equipment type + brand. "The top item will be
   auto-selected first when building proposals" — here that means the
   builder's picker lists equipment in exactly this order, the collection's
   own tier first. Arrows rank; the tier menu moves a unit between lists;
   unranked units wait below until they're placed. ─────────────────────── */

const GBB_COLS: { tier: string; title: string; head: string }[] = [
  { tier: "best", title: "Best List", head: "bg-gradient-to-b from-amber-100 to-amber-50 text-amber-900" },
  { tier: "better", title: "Better List", head: "bg-sky-50 text-sky-900" },
  { tier: "good", title: "Good List", head: "bg-slate-100 text-slate-700" },
];

function PackageOrderingCard() {
  const canManage = useCan("settings.money");
  const qc = useQueryClient();
  const cats = useQuoteCategories();
  const brands = useQuoteBrands();
  // The type pills: every active equipment slot that maps to a pricebook type.
  const types = useMemo(() => {
    const seen = new Set<string>();
    return (cats.data ?? [])
      .filter((c) => c.is_active && !c.parent_key && c.kind === "equipment" && c.pricebook_category)
      .filter((c) => (seen.has(c.pricebook_category!) ? false : (seen.add(c.pricebook_category!), true)));
  }, [cats.data]);
  const [type, setType] = useState<string | null>(null);
  const [brand, setBrand] = useState<string | null>(null);
  const activeType = type ?? types[0]?.pricebook_category ?? null;
  const { data: items, isLoading } = useEquipmentItems(activeType, "", brand);

  const save = useMutation({
    mutationFn: async (rows: { id: string; tier?: string | null; gbb_order?: number | null }[]) => {
      for (const r of rows) {
        const patch: Record<string, unknown> = {};
        if ("tier" in r) patch.tier = r.tier;
        if ("gbb_order" in r) patch.gbb_order = r.gbb_order;
        const { error } = await supabase.from("item").update(patch).eq("id", r.id);
        if (error) throw error;
      }
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["equipment_items"] });
      void qc.invalidateQueries({ queryKey: ["items"] });
    },
  });

  const all = items ?? [];
  const colOf = (tier: string) => all.filter((it) => it.tier === tier);
  const unranked = all.filter((it) => !it.tier);

  /** Move a unit up/down its list — writes only the orders that change
   *  (a whole-column renumber the first time, when orders are still null). */
  const moveRow = (tier: string, idx: number, dir: -1 | 1) => {
    const col = colOf(tier);
    const j = idx + dir;
    if (j < 0 || j >= col.length) return;
    const next = [...col];
    [next[idx], next[j]] = [next[j], next[idx]];
    const writes = next
      .map((it, i) => ({ id: it.id, gbb_order: i }))
      .filter((w) => (all.find((x) => x.id === w.id)?.gbb_order ?? null) !== w.gbb_order);
    if (writes.length) save.mutate(writes);
  };

  /** Move a unit to another tier (lands at the bottom) or back to unranked. */
  const moveTier = (it: Item, target: string) => {
    if (target === "none") {
      save.mutate([{ id: it.id, tier: null, gbb_order: null }]);
    } else {
      save.mutate([{ id: it.id, tier: target, gbb_order: colOf(target).length }]);
    }
  };

  return (
    <Card className="p-5">
      <div className="mb-1 flex flex-wrap items-center gap-2">
        <ListOrdered className="h-4 w-4 text-brand-600" />
        <h4 className="text-sm font-bold text-slate-800">Package Ordering — Good / Better / Best</h4>
        <InfoTip label="About package ordering">
          Rank the equipment the way you sell it. The builder's picker lists a
          collection's own tier first, in this exact order, so the right unit
          is usually the top row. A unit with no tier waits under Unranked and
          still shows in the picker, last.
        </InfoTip>
      </div>
      <div className="mt-2 flex flex-wrap items-center gap-2">
        {types.map((t) => (
          <button key={t.id} type="button" onClick={() => setType(t.pricebook_category)}
            className={`rounded-full px-3 py-1 text-xs font-bold transition-colors ${activeType === t.pricebook_category ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-500 hover:bg-slate-200"}`}>
            {t.label}
          </button>
        ))}
        <span className="mx-1 h-4 w-px bg-slate-200" />
        <button type="button" onClick={() => setBrand(null)}
          className={`rounded-full px-3 py-1 text-xs font-bold transition-colors ${brand === null ? "bg-brand-700 text-white" : "bg-slate-100 text-slate-500 hover:bg-slate-200"}`}>
          All Brands
        </button>
        {(brands.data ?? []).map((b) => (
          <button key={b.id} type="button" onClick={() => setBrand(b.name)}
            className={`rounded-full px-3 py-1 text-xs font-bold transition-colors ${brand === b.name ? "bg-brand-700 text-white" : "bg-slate-100 text-slate-500 hover:bg-slate-200"}`}>
            {b.name}
          </button>
        ))}
      </div>
      {isLoading ? (
        <div className="mt-3 h-32 animate-pulse rounded-xl bg-slate-50" />
      ) : !activeType ? (
        <p className="mt-3 text-xs text-slate-400">Add an equipment category first — each one gets its own ranking.</p>
      ) : (
        <>
          <div className="mt-3 grid gap-3 lg:grid-cols-3">
            {GBB_COLS.map(({ tier, title, head }) => {
              const col = colOf(tier);
              return (
                <div key={tier} className="overflow-hidden rounded-xl border border-hairline">
                  <div className={`flex items-center justify-between px-3 py-2 text-[11px] font-extrabold uppercase tracking-wider ${head}`}>
                    {title}
                    <span className="font-bold normal-case tracking-normal opacity-60">{col.length} item{col.length === 1 ? "" : "s"}</span>
                  </div>
                  <div className="max-h-72 divide-y divide-slate-50 overflow-y-auto">
                    {col.length === 0 && <p className="px-3 py-3 text-[11px] text-slate-400">Nothing here yet — place units from Unranked below.</p>}
                    {col.map((it, i) => (
                      <div key={it.id} className="flex items-center gap-2 px-2.5 py-1.5">
                        <span className="w-4 shrink-0 text-right text-[10px] font-bold tabular-nums text-slate-300">{i + 1}</span>
                        <div className="min-w-0 flex-1">
                          <div className="truncate text-[12px] font-semibold text-slate-800">{it.description ?? it.part_number}</div>
                          <div className="truncate font-mono text-[10px] text-slate-400">{it.model ?? it.part_number}</div>
                        </div>
                        {canManage && (
                          <span className="flex shrink-0 items-center">
                            <button type="button" disabled={i === 0 || save.isPending} onClick={() => moveRow(tier, i, -1)} className="rounded p-0.5 text-slate-300 hover:text-slate-600 disabled:opacity-30"><ArrowUp className="h-3 w-3" /></button>
                            <button type="button" disabled={i === col.length - 1 || save.isPending} onClick={() => moveRow(tier, i, 1)} className="rounded p-0.5 text-slate-300 hover:text-slate-600 disabled:opacity-30"><ArrowDown className="h-3 w-3" /></button>
                            <select
                              value={tier}
                              disabled={save.isPending}
                              onChange={(e) => moveTier(it, e.target.value)}
                              title="Move to another list"
                              className="ml-0.5 rounded border border-transparent bg-transparent py-0.5 text-[10px] font-semibold text-slate-400 hover:border-hairline focus:outline-none"
                            >
                              <option value="best">Best</option>
                              <option value="better">Better</option>
                              <option value="good">Good</option>
                              <option value="none">— none</option>
                            </select>
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
          {unranked.length > 0 && (
            <div className="mt-3 rounded-xl border border-dashed border-slate-200 px-3 py-2">
              <div className="mb-1 text-[11px] font-extrabold uppercase tracking-wider text-slate-400">Unranked · {unranked.length}</div>
              <div className="flex flex-wrap gap-1.5">
                {unranked.map((it) => (
                  <span key={it.id} className="inline-flex items-center gap-1.5 rounded-full bg-slate-50 py-1 pl-2.5 pr-1 text-[11px] font-semibold text-slate-600 ring-1 ring-slate-200">
                    {it.description ?? it.part_number}
                    {canManage && (
                      <select
                        value="none"
                        disabled={save.isPending}
                        onChange={(e) => { if (e.target.value !== "none") moveTier(it, e.target.value); }}
                        className="rounded-full border border-hairline bg-white px-1 py-0.5 text-[10px] font-bold text-slate-500 focus:outline-none"
                      >
                        <option value="none">place ▾</option>
                        <option value="best">Best</option>
                        <option value="better">Better</option>
                        <option value="good">Good</option>
                      </select>
                    )}
                  </span>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </Card>
  );
}

/* ── QUOTE-PKG-01 — reusable packages: define the fix once, techs fill an
   option with one tap. Repair packages show in the phone repair builder,
   Replace in the collections builder, Both everywhere. ─────────────────── */

const BLANK_PKG: { label: string; kind: QuotePackage["kind"]; tier: string; blurb: string; lines: QuotePackageLine[] } =
  { label: "", kind: "repair", tier: "", blurb: "", lines: [] };
const PKG_ROLES = [
  { v: "required", l: "In the package" },
  { v: "recommended", l: "Recommended add" },
  { v: "optional", l: "Optional add" },
];

function PackagesCard() {
  const canManage = useCan("settings.money");
  const { data } = useQuotePackages(undefined, true);
  const save = useSaveQuotePackage();
  const del = useDeleteQuotePackage();
  const [editing, setEditing] = useState<string | "new" | null>(null);
  const [draft, setDraft] = useState(BLANK_PKG);
  const [confirmDel, setConfirmDel] = useState<string | null>(null);
  const pkgs = data ?? [];

  const openEdit = (p?: QuotePackage) => {
    setDraft(p ? { label: p.label, kind: p.kind, tier: p.tier ?? "", blurb: p.blurb ?? "", lines: p.lines.map((l) => ({ ...l })) } : BLANK_PKG);
    setEditing(p ? p.id : "new");
  };
  const patchLine = (i: number, patch: Partial<QuotePackageLine>) =>
    setDraft((d) => ({ ...d, lines: d.lines.map((l, j) => (j === i ? { ...l, ...patch } : l)) }));

  return (
    <Card className="p-5">
      <div className="mb-1 flex items-center justify-between gap-3">
        <h4 className="flex items-center gap-2 text-sm font-bold text-slate-800">
          <Layers className="h-4 w-4 text-brand-600" /> Repair Packages
          <InfoTip label="About repair packages">
            Define a fix once — label, the customer-facing line, parts and prices. On the
            phone, one tap fills a quote option from it; the tech only adds their own
            words. (Equipment ranking for system quotes lives in Package Ordering above.)
          </InfoTip>
        </h4>
        {canManage && (
          <button type="button" onClick={() => openEdit()} className="inline-flex items-center gap-1 rounded-full bg-brand-700 px-3 py-1.5 text-xs font-bold text-white hover:bg-brand-800">
            <Plus className="h-3.5 w-3.5" /> New Package
          </button>
        )}
      </div>
      {pkgs.length === 0 && editing === null && (
        <p className="py-3 text-xs text-slate-500">
          None yet. Start with the repairs your techs quote every week — an ignitor kit, a
          blower motor, a capacitor + contactor.
        </p>
      )}
      <ul className="divide-y divide-slate-100">
        {pkgs.map((p) => (
          <li key={p.id} className="flex items-center gap-3 py-2.5">
            <span className={cnPkg(p.kind)}>{p.kind === "both" ? "Both" : p.kind === "replace" ? "Replace" : "Repair"}</span>
            <span className="min-w-0 flex-1">
              <span className={p.is_active ? "text-sm font-semibold text-slate-800" : "text-sm font-semibold text-slate-400 line-through"}>{p.label}</span>
              <span className="ml-2 text-xs text-slate-400">{p.lines.length} line{p.lines.length === 1 ? "" : "s"}</span>
            </span>
            <span className="text-sm font-bold tabular-nums text-slate-700">{money(pkgTotal(p))}</span>
            {canManage && (
              <span className="flex items-center gap-1">
                <button type="button" title={p.is_active ? "Retire — hides it from the builders" : "Reactivate"}
                  onClick={() => save.mutate({ id: p.id, label: p.label, kind: p.kind, tier: p.tier, blurb: p.blurb, sort: p.sort, is_active: !p.is_active, lines: p.lines })}
                  className="rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600">
                  {p.is_active ? <X className="h-3.5 w-3.5" /> : <RotateCcw className="h-3.5 w-3.5" />}
                </button>
                <button type="button" title="Edit" onClick={() => openEdit(p)} className="rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-brand-700">
                  <Pencil className="h-3.5 w-3.5" />
                </button>
                <button type="button"
                  title={confirmDel === p.id ? "Really delete — quotes already built from it keep their lines" : "Delete"}
                  onClick={() => { if (confirmDel === p.id) { del.mutate(p.id); setConfirmDel(null); } else setConfirmDel(p.id); }}
                  onBlur={() => setConfirmDel(null)}
                  className={confirmDel === p.id ? "rounded bg-red-50 px-1.5 py-1 text-[11px] font-bold text-red-600" : "rounded p-1 text-slate-300 hover:bg-red-50 hover:text-red-500"}>
                  {confirmDel === p.id ? "Sure?" : <X className="h-3.5 w-3.5" />}
                </button>
              </span>
            )}
          </li>
        ))}
      </ul>
      {editing !== null && (
        <div className="mt-3 rounded-xl border border-brand-200 bg-brand-50/40 p-4">
          <div className="grid gap-2.5 sm:grid-cols-[minmax(0,1fr)_150px_140px]">
            <Field label="Name (what the tech taps)">
              <input value={draft.label} onChange={(e) => setDraft((d) => ({ ...d, label: e.target.value }))} autoFocus
                placeholder="Blower Motor Replacement" className="w-full rounded-md border border-hairline px-2 py-1.5 text-sm focus:border-brand-400 focus:outline-none" />
            </Field>
            <Field label="Shows in">
              <select value={draft.kind} onChange={(e) => setDraft((d) => ({ ...d, kind: e.target.value as QuotePackage["kind"] }))}
                className="w-full rounded-md border border-hairline bg-white px-2 py-1.5 text-sm focus:border-brand-400 focus:outline-none">
                <option value="repair">Repair builder</option>
                <option value="replace">Replace builder</option>
                <option value="both">Both</option>
              </select>
            </Field>
            <Field label="Fills (replace pkgs)">
              <select value={draft.tier} onChange={(e) => setDraft((d) => ({ ...d, tier: e.target.value }))}
                title="Which collection this package aims at — the builder's shelf fills the matching one first"
                className="w-full rounded-md border border-hairline bg-white px-2 py-1.5 text-sm focus:border-brand-400 focus:outline-none">
                <option value="">Any collection</option>
                <option value="best">Best Collection</option>
                <option value="better">Better Collection</option>
                <option value="good">Good Collection</option>
              </select>
            </Field>
          </div>
          <Field label="Customer-facing line (the tech can reword it on site)">
            <input value={draft.blurb} onChange={(e) => setDraft((d) => ({ ...d, blurb: e.target.value }))}
              placeholder="Ends the noise for good — OEM motor, covered for 2 years." className="w-full rounded-md border border-hairline px-2 py-1.5 text-sm focus:border-brand-400 focus:outline-none" />
          </Field>
          <div className="mt-2 space-y-1.5">
            {draft.lines.map((l, i) => (
              <div key={i} className="flex items-center gap-2 rounded-lg bg-white px-2.5 py-1.5 ring-1 ring-slate-200">
                <input value={l.description} onChange={(e) => patchLine(i, { description: e.target.value })}
                  className="min-w-0 flex-1 rounded border-0 bg-transparent px-1 py-0.5 text-[13px] font-medium text-slate-800 focus:outline-none" />
                <input type="number" value={l.qty} onChange={(e) => patchLine(i, { qty: Number(e.target.value) || 1 })}
                  className="w-12 rounded border border-hairline px-1 py-0.5 text-right text-xs tabular-nums focus:outline-none" title="Qty" />
                <span className="text-xs text-slate-400">×</span>
                <input type="number" value={l.unit_price} onChange={(e) => patchLine(i, { unit_price: Number(e.target.value) || 0 })}
                  className="w-20 rounded border border-hairline px-1 py-0.5 text-right text-xs font-bold tabular-nums focus:outline-none" title="Price" />
                <select value={l.role} onChange={(e) => patchLine(i, { role: e.target.value })}
                  className="rounded border border-hairline bg-white px-1 py-0.5 text-[11px] focus:outline-none" title="How it rides on the option">
                  {PKG_ROLES.map((r) => <option key={r.v} value={r.v}>{r.l}</option>)}
                </select>
                <label className="flex items-center gap-1 text-[11px] font-medium text-slate-500" title="Included — $0 to the customer, value struck through">
                  <input type="checkbox" checked={l.inc} onChange={(e) => patchLine(i, { inc: e.target.checked })} /> $0
                </label>
                <button type="button" onClick={() => setDraft((d) => ({ ...d, lines: d.lines.filter((_, j) => j !== i) }))}
                  className="rounded p-0.5 text-slate-300 hover:text-red-500"><X className="h-3.5 w-3.5" /></button>
              </div>
            ))}
          </div>
          <PkgLineAdd onAdd={(l) => setDraft((d) => ({ ...d, lines: [...d.lines, l] }))} />
          <div className="mt-3 flex items-center justify-between">
            <span className="text-sm font-bold text-slate-800">Package total: {money(pkgTotal(draft))}</span>
            <span className="flex gap-2">
              <button type="button" onClick={() => setEditing(null)} className="rounded-md px-3 py-1.5 text-sm text-slate-500 hover:bg-slate-100">Cancel</button>
              <button type="button" disabled={!draft.label.trim() || draft.lines.length === 0 || save.isPending}
                onClick={() => save.mutate(
                  {
                    id: editing === "new" ? undefined : editing,
                    label: draft.label.trim(),
                    kind: draft.kind,
                    tier: draft.tier || null,
                    blurb: draft.blurb.trim() || null,
                    // Keep the package's shelf position on edit (saving used to reset sort to 0).
                    sort: editing === "new" ? pkgs.length : (pkgs.find((x) => x.id === editing)?.sort ?? 0),
                    lines: draft.lines,
                  },
                  { onSuccess: () => setEditing(null) },
                )}
                className="rounded-md bg-brand-700 px-4 py-1.5 text-sm font-semibold text-white hover:bg-brand-800 disabled:opacity-50">
                {save.isPending ? "Saving…" : "Save Package"}
              </button>
            </span>
          </div>
        </div>
      )}
    </Card>
  );
}

function cnPkg(kind: string): string {
  const base = "shrink-0 rounded-full px-2 py-0.5 text-[10.5px] font-bold ";
  if (kind === "replace") return base + "bg-indigo-50 text-indigo-700";
  if (kind === "both") return base + "bg-violet-50 text-violet-700";
  return base + "bg-amber-50 text-amber-800";
}

/** Add a line from the pricebook (searches items, snapshots price/photo) or type a custom one. */
function PkgLineAdd({ onAdd }: { onAdd: (l: QuotePackageLine) => void }) {
  const [term, setTerm] = useState("");
  const { data: hits } = useItems(term, null);
  const shown = term.trim().length >= 2 ? (hits ?? []).slice(0, 6) : [];
  const add = (it: Item | null) => {
    onAdd({
      item_id: it?.id ?? null,
      kind: "custom",
      description: it?.description ?? term.trim(),
      model: it?.model ?? it?.part_number ?? null,
      manufacturer: it?.manufacturer ?? null,
      image_url: it?.image_url ?? null,
      qty: 1,
      unit_price: Number(it?.sell_price ?? it?.base_price ?? 0),
      role: "required",
      inc: false,
      warranty: null,
      sort: 0,
    });
    setTerm("");
  };
  return (
    <div className="relative mt-2">
      <input value={term} onChange={(e) => setTerm(e.target.value)}
        onKeyDown={(e) => { if (e.key === "Enter" && term.trim()) { e.preventDefault(); add(shown[0] ?? null); } }}
        placeholder="Search the pricebook, or type a line and press Enter…"
        className="w-full rounded-lg border border-dashed border-slate-300 bg-white px-3 py-2 text-sm focus:border-brand-400 focus:outline-none" />
      {shown.length > 0 && (
        <ul className="absolute inset-x-0 top-full z-10 mt-1 overflow-hidden rounded-xl border border-hairline bg-white shadow-lg">
          {shown.map((it) => (
            <li key={it.id}>
              <button type="button" onClick={() => add(it)} className="flex w-full items-baseline gap-2 px-3 py-1.5 text-left hover:bg-brand-50/50">
                <span className="min-w-0 flex-1 truncate text-[13px] text-slate-800">{it.description}</span>
                <span className="shrink-0 text-[11px] text-slate-400">{it.model ?? it.part_number ?? ""}</span>
                <span className="shrink-0 text-[12px] font-bold tabular-nums text-slate-700">{money(Number(it.sell_price ?? it.base_price ?? 0))}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/** SETTINGS-NAV-02: how long a proposal stays valid (org_setting
 *  proposal_valid_days — new proposals expire this many days out, and
 *  "Extend" resets the clock by it). Had no screen until now. */
function ProposalTermsCard() {
  const canManage = useCan("settings.money");
  const stored = useOrgSettingNumber("proposal_valid_days", 30).data ?? 30;
  const save = useSaveOrgSetting();
  const [draft, setDraft] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const n = parseInt(draft ?? String(stored), 10);
  const dirty = draft !== null && Number.isFinite(n) && n >= 1 && n <= 365 && n !== stored;
  return (
    <Card className="p-4">
      <h3 className="flex items-center gap-2 text-sm font-bold text-slate-900">
        <CalendarClock className="h-4 w-4 text-slate-400" /> Proposal Terms
      </h3>
      <div className="mt-2 flex flex-wrap items-center gap-2 text-sm text-slate-700">
        <span>A proposal stays valid for</span>
        <input type="number" min={1} max={365} value={draft ?? String(stored)} disabled={!canManage}
          onChange={(e) => { setDraft(e.target.value); setSaved(false); }}
          className="h-8 w-16 rounded-md border border-hairline px-2 text-center text-sm font-semibold tabular-nums disabled:bg-slate-50" />
        <span>days</span>
        <InfoTip label="When a proposal expires">After that it reads Expired until someone extends it.</InfoTip>
        {canManage && dirty && (
          <button type="button" disabled={save.isPending}
            onClick={() => save.mutate({ key: "proposal_valid_days", value: n }, { onSuccess: () => { setDraft(null); setSaved(true); } })}
            className="rounded-md bg-brand-600 px-3 py-1 text-xs font-semibold text-white hover:bg-brand-700 disabled:opacity-50">
            {save.isPending ? "Saving…" : "Save"}
          </button>
        )}
        {saved && !dirty && <span className="inline-flex items-center gap-1 text-xs font-medium text-emerald-600"><Check className="h-3.5 w-3.5" /> Saved</span>}
      </div>
    </Card>
  );
}

// ---- Quote categories (the builder's slots: equipment / accessory / enhancement) ----

type SaveCat = ReturnType<typeof useSaveQuoteCategory>;
type MoveFn = (c: QuoteCategory, dir: -1 | 1, siblings: QuoteCategory[]) => void;

function CategoriesCard() {
  const canManage = useCan("settings.money");
  const { data } = useQuoteCategories(true);
  const equipTypes = useEquipTypes();
  const accSubs = useAccessorySubcategories();
  const save = useSaveQuoteCategory();
  const [adding, setAdding] = useState("");
  // SETTINGS-CALM-01: which row has its wiring open (one at a time).
  const [wiring, setWiring] = useState<string | null>(null);
  const list = data ?? [];
  const top = list.filter((c) => !c.parent_key);
  const topActive = top.filter((c) => c.is_active);
  const topArchived = top.filter((c) => !c.is_active);
  const childrenOf = (key: string) => list.filter((c) => c.parent_key === key).sort((a, b) => a.sort - b.sort);
  const move: MoveFn = (c, dir, siblings) => {
    const idx = siblings.findIndex((x) => x.id === c.id);
    const swap = siblings[idx + dir];
    if (!swap) return;
    save.mutate({ id: c.id, label: c.label, sort: swap.sort });
    save.mutate({ id: swap.id, label: swap.label, sort: c.sort });
  };
  const patch = (c: QuoteCategory, p: Partial<QuoteCategory>) => save.mutate({ id: c.id, label: c.label, ...p });

  return (
    <Card className="p-4">
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <Layers className="h-4 w-4 text-brand-600" />
        <h3 className="text-sm font-bold text-slate-900">Quote Categories</h3>
        <InfoTip label="About quote categories">
          The slots in the quote builder, each pulling from one pricebook type. “Type” changes how a slot is shown, “Shows on”
          limits it to install or service quotes, and “Default” slots appear on their own (the rest are under “Add slot”).
        </InfoTip>
      </div>
      {/* SETTINGS-CALM-01 (Seth 2026-09-30: "way overly complicated compared
          to the proposal builder I built"): his app's Categories screen is
          two lists of NAMES — the wiring is invisible. Same here now: a row
          is a name and two quiet facts; the pricebook mapping, icon and the
          rest live behind the pencil. Nothing was removed. */}
      <div className="space-y-1.5">
        {topActive.map((c, i) => (
          <div key={c.id} className="rounded-lg border border-hairline px-2.5 py-1.5">
            <div className="flex flex-wrap items-center gap-2">
              <div className="flex flex-col">
                <button disabled={!canManage || i === 0} onClick={() => move(c, -1, topActive)} className="text-slate-300 transition-colors hover:text-slate-600 disabled:opacity-30"><ArrowUp className="h-3 w-3" /></button>
                <button disabled={!canManage || i === topActive.length - 1} onClick={() => move(c, 1, topActive)} className="text-slate-300 transition-colors hover:text-slate-600 disabled:opacity-30"><ArrowDown className="h-3 w-3" /></button>
              </div>
              <input
                defaultValue={c.label}
                disabled={!canManage}
                onBlur={(e) => { const v = e.target.value.trim(); if (v && v !== c.label) save.mutate({ id: c.id, label: v }); }}
                className="min-w-[110px] flex-1 rounded-md border border-transparent bg-transparent px-1 py-0.5 text-sm font-semibold text-slate-800 hover:border-hairline focus:border-brand-400 focus:bg-white focus:outline-none"
              />
              <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10.5px] font-bold text-slate-500">{CATEGORY_TYPE_LABEL[c.kind] ?? c.kind}</span>
              <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10.5px] font-bold text-slate-500">{APPLIES_TO_LABEL[c.applies_to] ?? c.applies_to}</span>
              {canManage && (
                <button
                  onClick={() => patch(c, { default_on: !c.default_on })}
                  title={c.default_on ? "Shows automatically on new quotes" : "Available via Add slot, not shown by default"}
                  className={`rounded-full px-2 py-0.5 text-[10.5px] font-bold ${c.default_on ? "bg-sky-50 text-sky-700" : "bg-slate-100 text-slate-400"}`}
                >
                  {c.default_on ? "Default" : "On-demand"}
                </button>
              )}
              {canManage && (
                <button
                  onClick={() => setWiring(wiring === c.id ? null : c.id)}
                  title="Edit this slot's wiring — pricebook mapping, type, icon, archive"
                  className={`rounded-md p-1 transition-colors ${wiring === c.id ? "bg-brand-50 text-brand-700" : "text-slate-300 hover:bg-slate-100 hover:text-slate-600"}`}
                >
                  <Pencil className="h-3.5 w-3.5" />
                </button>
              )}
            </div>
            {canManage && wiring === c.id && (
              <div className="mt-2 flex flex-wrap items-center gap-2 rounded-lg bg-slate-50 px-2.5 py-2">
                <Sel label="Type" value={c.kind} disabled={!canManage} onChange={(v) => patch(c, { kind: v })}
                  options={CATEGORY_TYPES.map((t) => ({ v: t, l: CATEGORY_TYPE_LABEL[t] }))} />
                <Sel label="Shows on" value={c.applies_to} disabled={!canManage} onChange={(v) => patch(c, { applies_to: v })}
                  options={APPLIES_TO.map((t) => ({ v: t, l: APPLIES_TO_LABEL[t] }))} />
                <div className="flex items-center gap-1">
                  <span className="text-[10px] uppercase tracking-wide text-slate-400">Pricebook</span>
                  <input
                    list="equip-types"
                    defaultValue={c.pricebook_category ?? ""}
                    disabled={!canManage}
                    onBlur={(e) => { const v = e.target.value.trim(); if (v !== (c.pricebook_category ?? "")) patch(c, { pricebook_category: v || null }); }}
                    placeholder="equip type"
                    className="w-28 rounded-md border border-hairline bg-white px-1.5 py-0.5 text-xs text-slate-700 focus:border-brand-400 focus:outline-none"
                  />
                </div>
                <Sel label="Icon" value={c.icon ?? ""} disabled={!canManage} onChange={(v) => patch(c, { icon: v || null })}
                  options={CAT_ICON_NAMES.map((n) => ({ v: n, l: n }))} />
                <button
                  onClick={() => patch(c, { is_active: false })}
                  className="rounded-full px-2 py-0.5 text-[11px] font-semibold text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600"
                >
                  Archive
                </button>
              </div>
            )}
            {(c.kind === "accessory" || c.kind === "enhancement") && (
              <SubcatBlock parent={c} kids={childrenOf(c.key)} canManage={canManage} save={save} move={move} showWiring={wiring === c.id} />
            )}
          </div>
        ))}
        {topActive.length === 0 && <p className="text-xs text-slate-400">No active categories.</p>}
      </div>
      {topArchived.length > 0 && (
        <div className="mt-2 border-t border-slate-100 pt-2">
          <p className="mb-1 text-[11px] font-medium uppercase tracking-wide text-slate-400">Archived categories</p>
          <ul className="space-y-1">
            {topArchived.map((c) => (
              <li key={c.id} className="flex items-center gap-2 text-sm text-slate-400">
                <span className="min-w-0 flex-1 truncate line-through">{c.label}</span>
                {canManage && (
                  <button type="button" onClick={() => patch(c, { is_active: true })} className="inline-flex items-center gap-1 rounded-md border border-hairline bg-white px-2 py-0.5 text-[11px] font-medium text-slate-600 hover:bg-slate-50"><RotateCcw className="h-3 w-3" /> Restore</button>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
      <datalist id="equip-types">
        {(equipTypes.data ?? []).map((t) => <option key={t} value={t} />)}
      </datalist>
      <datalist id="acc-subcats">
        {(accSubs.data ?? []).map((s) => <option key={s} value={s} />)}
      </datalist>
      {canManage && (
        <div className="mt-2 flex items-center gap-2">
          <input
            value={adding}
            onChange={(e) => setAdding(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" && adding.trim()) save.mutate({ label: adding.trim(), pricebook_category: adding.trim(), sort: topActive.length }, { onSuccess: () => setAdding("") }); }}
            placeholder="Add a category (e.g. Mini Split, RTU, Garage Heater, Boiler)"
            className="flex-1 rounded-md border border-hairline bg-white px-2 py-1.5 text-sm focus:border-brand-400 focus:outline-none"
          />
          <button
            disabled={!adding.trim() || save.isPending}
            onClick={() => save.mutate({ label: adding.trim(), pricebook_category: adding.trim(), sort: topActive.length }, { onSuccess: () => setAdding("") })}
            className="inline-flex items-center gap-1 rounded-md bg-brand-700 px-3 py-1.5 text-xs font-semibold text-white transition-colors hover:bg-brand-800 disabled:bg-brand-300"
          >
            <Plus className="h-3.5 w-3.5" /> Add
          </button>
        </div>
      )}
    </Card>
  );
}

/** The dropdown cards under an accessory/enhancement slot (Thermostats, IAQ, HRV/ERV…) —
 * add / rename / reorder / hide, each mapped to a pricebook subcategory. */
function SubcatBlock({ parent, kids, canManage, save, move, showWiring = false }: { parent: QuoteCategory; kids: QuoteCategory[]; canManage: boolean; save: SaveCat; move: MoveFn; showWiring?: boolean }) {
  const [adding, setAdding] = useState("");
  const activeKids = kids.filter((k) => k.is_active);
  const archivedKids = kids.filter((k) => !k.is_active);
  const add = () => {
    const label = adding.trim();
    if (!label) return;
    save.mutate(
      { label, parent_key: parent.key, kind: "accessory", pricebook_category: parent.pricebook_category, pricebook_subcategory: label, sort: activeKids.length, applies_to: "both", default_on: true },
      { onSuccess: () => setAdding("") },
    );
  };
  return (
    <div className="mt-2 ml-7 border-l-2 border-hairline pl-3">
      <div className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-slate-400">Dropdown cards</div>
      <div className="space-y-1">
        {activeKids.map((k, i) => (
          <div key={k.id} className="flex flex-wrap items-center gap-2">
            <div className="flex flex-col">
              <button disabled={!canManage || i === 0} onClick={() => move(k, -1, activeKids)} className="text-slate-300 hover:text-slate-600 disabled:opacity-30"><ArrowUp className="h-3 w-3" /></button>
              <button disabled={!canManage || i === activeKids.length - 1} onClick={() => move(k, 1, activeKids)} className="text-slate-300 hover:text-slate-600 disabled:opacity-30"><ArrowDown className="h-3 w-3" /></button>
            </div>
            <input
              defaultValue={k.label}
              disabled={!canManage}
              onBlur={(e) => { const v = e.target.value.trim(); if (v && v !== k.label) save.mutate({ id: k.id, label: v }); }}
              className="min-w-[100px] flex-1 rounded-md border border-transparent bg-transparent px-1 py-0.5 text-xs font-medium text-slate-800 hover:border-hairline focus:border-brand-400 focus:bg-white focus:outline-none"
            />
            {/* SETTINGS-CALM-01: a card is a NAME; its pricebook mapping and
                archive show only while the parent row's wiring is open. */}
            {showWiring && (
              <div className="flex items-center gap-1">
                <span className="text-[10px] uppercase tracking-wide text-slate-400">Pricebook</span>
                <input
                  list="acc-subcats"
                  defaultValue={k.pricebook_subcategory ?? ""}
                  disabled={!canManage}
                  onBlur={(e) => { const v = e.target.value.trim(); if (v !== (k.pricebook_subcategory ?? "")) save.mutate({ id: k.id, label: k.label, pricebook_subcategory: v || null }); }}
                  placeholder="subcategory"
                  className="w-32 rounded-md border border-hairline bg-white px-1.5 py-0.5 text-[11px] text-slate-700 focus:border-brand-400 focus:outline-none"
                />
              </div>
            )}
            {canManage && showWiring && (
              <button
                onClick={() => save.mutate({ id: k.id, label: k.label, is_active: false })}
                className="rounded-full px-2 py-0.5 text-[11px] font-semibold text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600"
              >
                Archive
              </button>
            )}
          </div>
        ))}
        {activeKids.length === 0 && <p className="text-[11px] text-slate-400">No dropdown cards yet.</p>}
      </div>
      {canManage && (
        <div className="mt-1.5 flex items-center gap-2">
          <input
            value={adding}
            onChange={(e) => setAdding(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") add(); }}
            placeholder="Add a card (e.g. Surge Protectors)"
            className="flex-1 rounded-md border border-hairline bg-white px-2 py-1 text-xs focus:border-brand-400 focus:outline-none"
          />
          <button
            disabled={!adding.trim() || save.isPending}
            onClick={add}
            className="inline-flex items-center gap-1 rounded-md bg-slate-700 px-2.5 py-1 text-[11px] font-semibold text-white transition-colors hover:bg-slate-800 disabled:bg-slate-300"
          >
            <Plus className="h-3 w-3" /> Add
          </button>
        </div>
      )}
      {archivedKids.length > 0 && (
        <div className="mt-1.5">
          <p className="mb-1 text-[10px] font-medium uppercase tracking-wide text-slate-400">Archived</p>
          <ul className="space-y-0.5">
            {archivedKids.map((k) => (
              <li key={k.id} className="flex items-center gap-2 text-xs text-slate-400">
                <span className="min-w-0 flex-1 truncate line-through">{k.label}</span>
                {canManage && (
                  <button type="button" onClick={() => save.mutate({ id: k.id, label: k.label, is_active: true })} className="inline-flex items-center gap-1 rounded-md border border-hairline bg-white px-2 py-0.5 text-[10px] font-medium text-slate-600 hover:bg-slate-50"><RotateCcw className="h-3 w-3" /> Restore</button>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

/** Compact labelled select used in the categories editor rows. */
function Sel({ label, value, onChange, options, disabled }: { label: string; value: string; onChange: (v: string) => void; options: { v: string; l: string }[]; disabled?: boolean }) {
  return (
    <label className="flex items-center gap-1">
      <span className="text-[10px] uppercase tracking-wide text-slate-400">{label}</span>
      <select
        value={value}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value)}
        className="rounded-md border border-hairline bg-white px-1.5 py-0.5 text-xs text-slate-700 focus:border-brand-400 focus:outline-none"
      >
        {label === "Icon" && <option value="">—</option>}
        {options.map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}
      </select>
    </label>
  );
}

// ---- Manufacturer brands (the per-collection toggle in the quote builder) ----

function BrandsCard() {
  const canManage = useCan("settings.money");
  const { data } = useQuoteBrands(true);
  const save = useSaveQuoteBrand();
  const [adding, setAdding] = useState("");
  const list = data ?? [];
  const active = list.filter((b) => b.is_active);
  const archived = list.filter((b) => !b.is_active);
  const move = (b: QuoteBrand, dir: -1 | 1) => {
    const idx = active.findIndex((x) => x.id === b.id);
    const swap = active[idx + dir];
    if (!swap) return;
    save.mutate({ id: b.id, name: b.name, sort: swap.sort });
    save.mutate({ id: swap.id, name: swap.name, sort: b.sort });
  };
  return (
    <Card className="p-4">
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <Factory className="h-4 w-4 text-brand-600" />
        <h3 className="text-sm font-bold text-slate-900">Manufacturer Brands</h3>
        <InfoTip label="About manufacturer brands">The brand toggle on each collection in the quote builder.</InfoTip>
      </div>
      <div className="space-y-1.5">
        {active.map((b, i) => (
          <div key={b.id} className="flex items-center gap-2 rounded-lg border border-hairline px-2.5 py-1.5">
            <div className="flex flex-col">
              <button disabled={!canManage || i === 0} onClick={() => move(b, -1)} className="text-slate-300 transition-colors hover:text-slate-600 disabled:opacity-30"><ArrowUp className="h-3 w-3" /></button>
              <button disabled={!canManage || i === active.length - 1} onClick={() => move(b, 1)} className="text-slate-300 transition-colors hover:text-slate-600 disabled:opacity-30"><ArrowDown className="h-3 w-3" /></button>
            </div>
            <input
              defaultValue={b.name}
              disabled={!canManage}
              onBlur={(e) => { const v = e.target.value.trim(); if (v && v !== b.name) save.mutate({ id: b.id, name: v }); }}
              className="flex-1 rounded-md border border-transparent bg-transparent px-1 py-0.5 text-sm font-medium text-slate-800 hover:border-hairline focus:border-brand-400 focus:bg-white focus:outline-none"
            />
            {canManage && (
              <button
                onClick={() => save.mutate({ id: b.id, name: b.name, is_active: false })}
                className="rounded-full px-2 py-0.5 text-[11px] font-semibold text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600"
              >
                Archive
              </button>
            )}
          </div>
        ))}
        {active.length === 0 && <p className="text-xs text-slate-400">No active brands.</p>}
      </div>
      {archived.length > 0 && (
        <div className="mt-2 border-t border-slate-100 pt-2">
          <p className="mb-1 text-[11px] font-medium uppercase tracking-wide text-slate-400">Archived</p>
          <ul className="space-y-1">
            {archived.map((b) => (
              <li key={b.id} className="flex items-center gap-2 text-sm text-slate-400">
                <span className="min-w-0 flex-1 truncate line-through">{b.name}</span>
                {canManage && (
                  <button type="button" onClick={() => save.mutate({ id: b.id, name: b.name, is_active: true })} className="inline-flex items-center gap-1 rounded-md border border-hairline bg-white px-2 py-0.5 text-[11px] font-medium text-slate-600 hover:bg-slate-50"><RotateCcw className="h-3 w-3" /> Restore</button>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
      {canManage && (
        <div className="mt-2 flex items-center gap-2">
          <input
            value={adding}
            onChange={(e) => setAdding(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" && adding.trim()) save.mutate({ name: adding.trim(), sort: active.length }, { onSuccess: () => setAdding("") }); }}
            placeholder="Add a manufacturer (e.g. Mitsubishi, Bosch)"
            className="flex-1 rounded-md border border-hairline bg-white px-2 py-1.5 text-sm focus:border-brand-400 focus:outline-none"
          />
          <button
            disabled={!adding.trim() || save.isPending}
            onClick={() => save.mutate({ name: adding.trim(), sort: active.length }, { onSuccess: () => setAdding("") })}
            className="inline-flex items-center gap-1 rounded-md bg-brand-700 px-3 py-1.5 text-xs font-semibold text-white transition-colors hover:bg-brand-800 disabled:bg-brand-300"
          >
            <Plus className="h-3.5 w-3.5" /> Add
          </button>
        </div>
      )}
    </Card>
  );
}

// ---- Financing plans ----

type PlanDraft = Partial<FinancingPlan> & { name: string };
const BLANK_PLAN: PlanDraft = { name: "", apr: 0, term_months: 0, min_amount: 1000, plan_number: "" };

function FinancingPlansCard() {
  const canManage = useCan("settings.money");
  const { data, isLoading } = useFinancingPlans(true);
  const save = useSaveFinancingPlan();
  const [editId, setEditId] = useState<string | "new" | null>(null);
  const [draft, setDraft] = useState<PlanDraft>(BLANK_PLAN);

  const rows = data ?? [];
  const active = rows.filter((r) => r.is_active);
  const archived = rows.filter((r) => !r.is_active);

  const startNew = () => { setDraft(BLANK_PLAN); setEditId("new"); };
  const startEdit = (p: FinancingPlan) => { setDraft({ ...p }); setEditId(p.id); };
  const commit = () => {
    if (!draft.name.trim()) return;
    save.mutate(
      { ...draft, id: editId === "new" ? undefined : editId ?? undefined },
      { onSuccess: () => { setEditId(null); setDraft(BLANK_PLAN); } },
    );
  };

  return (
    <Card className="overflow-hidden">
      <div className="flex items-center justify-between border-b border-slate-100 bg-gradient-to-r from-sky-50/70 via-transparent to-transparent px-4 py-2.5">
        <div className="flex items-center gap-2">
          <span className="flex h-6 w-6 items-center justify-center rounded-lg bg-sky-50 text-sky-600">
            <CreditCard className="h-3.5 w-3.5" />
          </span>
          <span className="text-xs font-semibold uppercase tracking-wider text-slate-600">
            Financing Plans
          </span>
          <InfoTip label="About financing plans">
            The financing a tech can attach to a quote; the customer sees the estimated monthly payment. Plan numbers match what
            the office submits to the lender.
          </InfoTip>
        </div>
        {canManage && editId !== "new" && (
          <button
            type="button"
            onClick={startNew}
            className="inline-flex items-center gap-1 text-xs font-medium text-brand-700 transition-colors hover:text-brand-900"
          >
            <Plus className="h-3 w-3" /> Add plan
          </button>
        )}
      </div>

      <div className="px-4 py-3">
        {editId === "new" && (
          <PlanForm draft={draft} setDraft={setDraft} onSave={commit} onCancel={() => setEditId(null)} saving={save.isPending} />
        )}

        {isLoading ? (
          <div className="space-y-1.5">{Array.from({ length: 4 }).map((_, i) => <div key={i} className="h-9 animate-pulse rounded-lg bg-slate-100" />)}</div>
        ) : (
          <ul className="divide-y divide-slate-100">
            {active.map((p) => (
              <li key={p.id} className="group py-2">
                {editId === p.id ? (
                  <PlanForm draft={draft} setDraft={setDraft} onSave={commit} onCancel={() => setEditId(null)} saving={save.isPending} />
                ) : (
                  <div className="flex items-center gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-sm font-medium text-slate-800">{p.name}</div>
                      <div className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[11px] text-slate-400">
                        <span>{p.apr === 0 ? "0% APR" : `${p.apr}% APR`}</span>
                        <span>·</span>
                        <span>{p.term_months} mo</span>
                        <span>·</span>
                        <span>min {money(p.min_amount)}</span>
                        {p.plan_number && (<><span>·</span><span className="rounded bg-slate-100 px-1.5 py-0.5 font-mono text-slate-500">#{p.plan_number}</span></>)}
                      </div>
                    </div>
                    {canManage && (
                      <>
                        <button type="button" onClick={() => startEdit(p)} title="Edit" className="rounded p-1 text-slate-300 opacity-0 transition-all hover:text-slate-600 group-hover:opacity-100"><Pencil className="h-3.5 w-3.5" /></button>
                        <button type="button" onClick={() => save.mutate({ ...p, is_active: false })} title="Archive" className="rounded p-1 text-slate-300 opacity-0 transition-all hover:text-red-500 group-hover:opacity-100"><X className="h-3.5 w-3.5" /></button>
                      </>
                    )}
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}

        {archived.length > 0 && (
          <div className="mt-2 border-t border-slate-100 pt-2">
            <p className="mb-1 text-[11px] font-medium uppercase tracking-wide text-slate-400">Archived</p>
            <ul className="space-y-1">
              {archived.map((p) => (
                <li key={p.id} className="flex items-center gap-2 text-sm text-slate-400">
                  <span className="min-w-0 flex-1 truncate line-through">{p.name}</span>
                  {canManage && (
                    <button type="button" onClick={() => save.mutate({ ...p, is_active: true })} className="inline-flex items-center gap-1 rounded-md border border-hairline bg-white px-2 py-0.5 text-[11px] font-medium text-slate-600 hover:bg-slate-50"><RotateCcw className="h-3 w-3" /> Restore</button>
                  )}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </Card>
  );
}

function PlanForm({ draft, setDraft, onSave, onCancel, saving }: {
  draft: PlanDraft; setDraft: (d: PlanDraft) => void; onSave: () => void; onCancel: () => void; saving: boolean;
}) {
  const num = (v: string) => (v === "" ? 0 : Number(v));
  return (
    <form onSubmit={(e) => { e.preventDefault(); onSave(); }} className="space-y-2 rounded-lg border border-hairline bg-slate-50/60 p-3">
      <input autoFocus value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} placeholder="Plan name (e.g. 8.99% APR / 60 Months)" className="w-full rounded-md border border-hairline bg-white px-2 py-1.5 text-sm focus:border-brand-400 focus:outline-none focus:ring-2 focus:ring-brand-400/30" />
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
        <Field label="APR %"><input type="number" step="0.01" value={draft.apr ?? 0} onChange={(e) => setDraft({ ...draft, apr: num(e.target.value) })} className="w-full rounded-md border border-hairline bg-white px-2 py-1.5 text-sm" /></Field>
        <Field label="Term (mo)"><input type="number" value={draft.term_months ?? 0} onChange={(e) => setDraft({ ...draft, term_months: num(e.target.value) })} className="w-full rounded-md border border-hairline bg-white px-2 py-1.5 text-sm" /></Field>
        <Field label="Min $"><input type="number" value={draft.min_amount ?? 0} onChange={(e) => setDraft({ ...draft, min_amount: num(e.target.value) })} className="w-full rounded-md border border-hairline bg-white px-2 py-1.5 text-sm" /></Field>
        <Field label="Plan #"><input value={draft.plan_number ?? ""} onChange={(e) => setDraft({ ...draft, plan_number: e.target.value })} className="w-full rounded-md border border-hairline bg-white px-2 py-1.5 text-sm" /></Field>
      </div>

      {/* Special offer — highlighted on the proposal, but only between the start/end dates */}
      <div className="flex items-center gap-2">
        <label className="flex items-center gap-2 text-xs font-semibold text-slate-600">
          <input type="checkbox" checked={!!draft.is_special} onChange={(e) => setDraft({ ...draft, is_special: e.target.checked })} className="h-4 w-4 accent-amber-500" />
          Special offer
        </label>
        <InfoTip label="About special offers">Highlighted on the proposal, and shown only between the start and end dates.</InfoTip>
      </div>
      {draft.is_special && (
        <div className="grid grid-cols-1 gap-2 rounded-md bg-amber-50/70 p-2 sm:grid-cols-3">
          <Field label="Starts"><input type="date" value={draft.special_start ?? ""} onChange={(e) => setDraft({ ...draft, special_start: e.target.value })} className="w-full rounded-md border border-hairline bg-white px-2 py-1.5 text-sm" /></Field>
          <Field label="Ends"><input type="date" value={draft.special_end ?? ""} onChange={(e) => setDraft({ ...draft, special_end: e.target.value })} className="w-full rounded-md border border-hairline bg-white px-2 py-1.5 text-sm" /></Field>
          <Field label="Badge text"><input value={draft.special_description ?? ""} onChange={(e) => setDraft({ ...draft, special_description: e.target.value })} placeholder="Limited time — no interest!" className="w-full rounded-md border border-hairline bg-white px-2 py-1.5 text-sm" /></Field>
        </div>
      )}

      <div className="flex items-center gap-2">
        <button type="submit" disabled={!draft.name.trim() || saving} className="rounded-md bg-brand-700 px-3 py-1.5 text-xs font-semibold text-white transition-colors hover:bg-brand-800 disabled:bg-brand-300">{saving ? "Saving…" : "Save"}</button>
        <button type="button" onClick={onCancel} className="rounded-md px-2 py-1.5 text-xs font-medium text-slate-500 hover:text-slate-700">Cancel</button>
      </div>
    </form>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="mb-0.5 block text-[10px] font-semibold uppercase tracking-wide text-slate-400">{label}</span>
      {children}
    </label>
  );
}

// ---- Utility rebate sources ----

function UtilitiesCard() {
  const canManage = useCan("settings.money");
  const { data, isLoading } = useUtilities(true);
  const save = useSaveUtility();
  const [adding, setAdding] = useState(false);
  const [name, setName] = useState("");
  const [editId, setEditId] = useState<string | null>(null);
  const [editName, setEditName] = useState("");

  const rows = data ?? [];
  const active = rows.filter((r) => r.is_active);
  const archived = rows.filter((r) => !r.is_active);

  return (
    <Card className="overflow-hidden">
      <div className="flex items-center justify-between border-b border-slate-100 bg-gradient-to-r from-amber-50/70 via-transparent to-transparent px-4 py-2.5">
        <div className="flex items-center gap-2">
          <span className="flex h-6 w-6 items-center justify-center rounded-lg bg-amber-50 text-amber-600">
            <Zap className="h-3.5 w-3.5" />
          </span>
          <span className="text-xs font-semibold uppercase tracking-wider text-slate-600">
            Utility Rebates
          </span>
          <InfoTip label="About utility rebates">
            On a quote, the tech picks the utility and types that job&apos;s rebate amount. A rebate from a utility that supplies
            electric, on a job with an AC, heat pump or mini-split, adds the Rebate Test Form to the installer&apos;s Wrap Up.
          </InfoTip>
        </div>
        {canManage && (
          <button type="button" onClick={() => setAdding((v) => !v)} className="inline-flex items-center gap-1 text-xs font-medium text-brand-700 transition-colors hover:text-brand-900"><Plus className="h-3 w-3" /> Add utility</button>
        )}
      </div>

      <div className="px-4 py-3">
        {adding && (
          <form onSubmit={(e) => { e.preventDefault(); save.mutate({ name, sort: active.length }, { onSuccess: () => { setName(""); setAdding(false); } }); }} className="mb-3 flex items-center gap-2 rounded-lg border border-hairline bg-slate-50/60 p-2">
            <input autoFocus value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Connexus Energy" className="min-w-[180px] flex-1 rounded-md border border-hairline bg-white px-2 py-1.5 text-sm focus:border-brand-400 focus:outline-none focus:ring-2 focus:ring-brand-400/30" />
            <button type="submit" disabled={!name.trim() || save.isPending} className="rounded-md bg-brand-700 px-3 py-1.5 text-xs font-semibold text-white hover:bg-brand-800 disabled:bg-brand-300">{save.isPending ? "Saving…" : "Add"}</button>
          </form>
        )}

        {isLoading ? (
          <div className="space-y-1.5">{Array.from({ length: 5 }).map((_, i) => <div key={i} className="h-8 animate-pulse rounded-lg bg-slate-100" />)}</div>
        ) : (
          <ul className="divide-y divide-slate-100">
            {active.map((u) => (
              <li key={u.id} className="group flex items-center gap-2 py-1.5">
                {editId === u.id ? (
                  <form onSubmit={(e) => { e.preventDefault(); save.mutate({ ...u, name: editName }, { onSuccess: () => setEditId(null) }); }} className="flex flex-1 items-center gap-2">
                    <input autoFocus value={editName} onChange={(e) => setEditName(e.target.value)} className="flex-1 rounded-md border border-hairline px-2 py-1 text-sm" />
                    <button type="submit" className="rounded-md bg-brand-700 px-2 py-1 text-[11px] font-semibold text-white"><Check className="h-3 w-3" /></button>
                    <button type="button" onClick={() => setEditId(null)} className="p-1 text-slate-400 hover:text-slate-600"><X className="h-3 w-3" /></button>
                  </form>
                ) : (
                  <>
                    <span className="min-w-0 flex-1 truncate text-sm font-medium text-slate-800">{u.name}</span>
                    <select value={u.provides ?? "both"} disabled={!canManage}
                      onChange={(e) => save.mutate({ ...u, provides: e.target.value as Utility["provides"] })}
                      title="What this utility supplies"
                      aria-label={`What ${u.name} supplies`}
                      className="rounded border border-hairline bg-white px-1.5 py-0.5 text-[11px] text-slate-600">
                      <option value="electric">Supplies electric</option>
                      <option value="gas">Supplies gas</option>
                      <option value="both">Supplies both</option>
                    </select>
                    {canManage && (
                      <>
                        <button type="button" onClick={() => { setEditId(u.id); setEditName(u.name); }} title="Rename" className="rounded p-1 text-slate-300 opacity-0 transition-all hover:text-slate-600 group-hover:opacity-100"><Pencil className="h-3 w-3" /></button>
                        <button type="button" onClick={() => save.mutate({ ...u, is_active: false })} title="Archive" className="rounded p-1 text-slate-300 opacity-0 transition-all hover:text-red-500 group-hover:opacity-100"><X className="h-3.5 w-3.5" /></button>
                      </>
                    )}
                  </>
                )}
              </li>
            ))}
          </ul>
        )}

        {archived.length > 0 && (
          <div className="mt-2 border-t border-slate-100 pt-2">
            <p className="mb-1 text-[11px] font-medium uppercase tracking-wide text-slate-400">Archived</p>
            <ul className="space-y-1">
              {archived.map((u) => (
                <li key={u.id} className="flex items-center gap-2 text-sm text-slate-400">
                  <span className="min-w-0 flex-1 truncate line-through">{u.name}</span>
                  {canManage && (
                    <button type="button" onClick={() => save.mutate({ ...u, is_active: true })} className="inline-flex items-center gap-1 rounded-md border border-hairline bg-white px-2 py-0.5 text-[11px] font-medium text-slate-600 hover:bg-slate-50"><RotateCcw className="h-3 w-3" /> Restore</button>
                  )}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </Card>
  );
}
